import { useState, useEffect, useCallback, useRef } from "react";
import { Head, router } from "@inertiajs/react";
import Container from "../../components/Container";
import ProductGrid from "../../components/ProductGrid";
import FilterControls from "../../components/FilterControls";
import MobileFilterControls from "../../components/MobileFilterControls";
import StickyFilterBar from "../../components/StickyFilterBar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Filter } from "lucide-react";
import MainLayout from "../../components/MainLayout";
import Breadcrumb from "../../components/Breadcrumb";
import pkg from "lodash";
const { debounce } = pkg;

import { StoreSettings, Brand, Product, Category } from "../../types";

interface Pagination {
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
}

interface Filters {
  name?: string;
  category_id?: string;
  brand_id?: string;
  min_price?: string;
  max_price?: string;
}

interface ProductsProps {
  products: Product[];
  categories: Category[];
  brands: Brand[];
  pagination: Pagination;
  filters: Filters;
}

const Products = ({
  products,
  categories,
  brands,
  pagination,
  filters,
  store_settings,
  exclusive_products,
}: ProductsProps & {
  store_settings: StoreSettings;
  exclusive_products: any[];
}) => {
  const maxPrice = 10000; // Or get this dynamically if possible

  const [activeFilters, setActiveFilters] = useState({
    name: filters.name || "",
    category_id: filters.category_id || "all",
    brand_id: filters.brand_id || "all",
    priceRange: [
      Number(filters.min_price || 0),
      Number(filters.max_price || maxPrice),
    ] as [number, number],
  });

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const debouncedFilter = useCallback(
    debounce((newFilters) => {
      const params = {
        ...newFilters,
        category_id:
          newFilters.category_id === "all" ? "" : newFilters.category_id,
        brand_id: newFilters.brand_id === "all" ? "" : newFilters.brand_id,
        min_price: newFilters.priceRange[0],
        max_price: newFilters.priceRange[1],
      };
      delete params.priceRange; // Clean up the object before sending

      router.get(
        "/products",
        {
          ...params,
          category_id: params.category_id === "all" ? "" : params.category_id,
        },
        {
          preserveState: true,
          replace: true,
          preserveScroll: true,
        },
      );
    }, 300),
    [],
  );

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      debouncedFilter(activeFilters);
    }
  }, [activeFilters, debouncedFilter]);

  const handleFilterChange = (
    filterName: keyof typeof activeFilters,
    value: any,
  ) => {
    setActiveFilters((prev) => ({ ...prev, [filterName]: value }));
    // Auto-close mobile filters when a selection is made
    setMobileFiltersOpen(false);
  };

  const resetFilters = () => {
    const defaultFilters = {
      name: "",
      category_id: "all",
      brand_id: "all",
      priceRange: [0, maxPrice] as [number, number],
    };
    setActiveFilters(defaultFilters);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.total_pages) return;
    const params = { ...filters, page };
    router.get("/products", params, { preserveState: true, replace: true });
  };

  const filterProps = {
    nameFilter: activeFilters.name,
    setNameFilter: (value: string) => handleFilterChange("name", value),
    categoryFilter: activeFilters.category_id,
    setCategoryFilter: (value: string | string[]) =>
      handleFilterChange("category_id", value),
    brandFilter: activeFilters.brand_id,
    setBrandFilter: (value: string) => handleFilterChange("brand_id", value),
    priceRange: activeFilters.priceRange,
    setPriceRange: (value: [number, number]) =>
      handleFilterChange("priceRange", value),
    maxPrice,
    categories,
    brands,
    applyFilters: () => {}, // No longer needed, but passed to prevent crashes if component expects it
    resetFilters,
    onClose: () => setMobileFiltersOpen(false), // Add onClose for auto-close functionality
  };

  const stickyFilterProps = {
    ...filterProps,
    onOpenFilters: () => setMobileFiltersOpen(true),
  };

  const selectedCategories =
    activeFilters.category_id === "all" ? [] : [activeFilters.category_id];

  const selectedCategory =
    selectedCategories.length === 1
      ? categories.find((c) => c.id === Number(selectedCategories[0]))
      : null;

  const breadcrumbItems = selectedCategory
    ? [
        {
          label: selectedCategory.name,
          href: `/products?category_id=${selectedCategory.id}`,
        },
        { label: "Productos" },
      ]
    : selectedCategories.length > 1
      ? [
          { label: `${selectedCategories.length} categorías` },
          { label: "Productos" },
        ]
      : [{ label: "Productos" }];

  return (
    <MainLayout
      store_settings={store_settings}
      exclusiveProducts={exclusive_products}
    >
      <Head
        title={
          selectedCategory
            ? `Productos de ${selectedCategory.name}`
            : selectedCategories.length > 1
              ? `Productos de ${selectedCategories.length} categorías`
              : "Productos"
        }
      />

      {/* Mobile Sticky Filter Bar */}
      <StickyFilterBar {...stickyFilterProps} />

      <main className="flex-grow">
        <Container>
           <div className="py-6 sm:py-8 md:py-12">
             <Breadcrumb items={breadcrumbItems} />
             {selectedCategory && selectedCategory.pdf_url && (
               <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                 <div className="flex items-center justify-between">
                   <div>
                     <h2 className="text-lg font-semibold text-gray-900">
                       Catálogo de {selectedCategory.name}
                     </h2>
                     <p className="text-sm text-gray-700">
                       Descarga el PDF del catálogo de esta categoría.
                     </p>
                   </div>
                   <Button
                     onClick={() => window.open(selectedCategory.pdf_url, '_blank')}
                     variant="outline"
                     className="border-gray-600 text-gray-600 hover:bg-gray-50 font-bold"
                   >
                     Ver PDF del Catálogo
                   </Button>
                 </div>
               </div>
             )}
             <div className="flex justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">
                {selectedCategory
                  ? selectedCategory.name
                  : selectedCategories.length > 1
                    ? `${selectedCategories.length} Categorías`
                    : "Nuestros Productos"}
              </h1>
              <div className="md:hidden">
                <Sheet
                  open={mobileFiltersOpen}
                  onOpenChange={setMobileFiltersOpen}
                >
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Filter className="h-4 w-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-full max-w-md p-0 rounded-l-3xl border-0 shadow-2xl"
                  >
                    <MobileFilterControls {...filterProps} />
                  </SheetContent>
                </Sheet>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8">
              {/* Filters Sidebar for Desktop */}
              <aside className="hidden md:block md:col-span-1">
                <div className="sticky top-24">
                  <FilterControls {...filterProps} />
                </div>
              </aside>

              {/* Products Grid */}
              <div className="md:col-span-4">
                <ProductGrid
                  products={products}
                  store_settings={store_settings}
                />
                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="mt-6 sm:mt-8 flex justify-center items-center space-x-2 md:space-x-4">
                    <Button
                      size="sm"
                      onClick={() =>
                        handlePageChange(pagination.current_page - 1)
                      }
                      disabled={pagination.current_page <= 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm md:text-base px-2">
                      Página {pagination.current_page} de{" "}
                      {pagination.total_pages}
                    </span>
                    <Button
                      size="sm"
                      onClick={() =>
                        handlePageChange(pagination.current_page + 1)
                      }
                      disabled={
                        pagination.current_page >= pagination.total_pages
                      }
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </main>
    </MainLayout>
  );
};

export default Products;
